import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import './PhotoUploadModal.css';

const PhotoUploadModal = ({ isOpen, onClose, onCropComplete }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [fileName, setFileName] = useState('');

  const onFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setFileName(file.name);
      
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setImageSrc(reader.result);
      });
      reader.readAsDataURL(file);
    }
  };

  const onCropChange = (crop) => {
    setCrop(crop);
  };

  const onZoomChange = (zoom) => {
    setZoom(zoom);
  };

  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const createCroppedImage = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    const image = new Image();
    image.src = imageSrc;
    
    await new Promise((resolve) => {
      image.onload = resolve;
    });

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Set canvas size to 512x512 (our target size)
    const targetSize = 512;
    canvas.width = targetSize;
    canvas.height = targetSize;

    // Draw the cropped image
    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      targetSize,
      targetSize
    );

    // Convert to blob with compression
    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Convert blob to data URL for preview
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({
                dataUrl: reader.result,
                blob: blob,
                file: new File([blob], fileName, { type: 'image/jpeg' })
              });
            };
            reader.readAsDataURL(blob);
          }
        },
        'image/jpeg',
        0.9
      );
    });
  };

  const handleApply = async () => {
    const croppedImage = await createCroppedImage();
    if (croppedImage) {
      onCropComplete(croppedImage);
      handleClose();
    }
  };

  const handleClose = () => {
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setFileName('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="photo-upload-modal-overlay" onClick={handleClose}>
      <div className="photo-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="photo-upload-modal-header">
          <h2>Upload & Crop Photo</h2>
          <button className="modal-close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="photo-upload-modal-body">
          {!imageSrc ? (
            <div className="photo-upload-dropzone">
              <input
                type="file"
                accept="image/*"
                onChange={onFileChange}
                id="photo-file-input"
                style={{ display: 'none' }}
              />
              <label htmlFor="photo-file-input" className="photo-upload-label">
                <div className="upload-icon">📷</div>
                <p className="upload-text">Click to select a photo</p>
                <p className="upload-hint">Max size: 2MB • JPG, PNG</p>
              </label>
            </div>
          ) : (
            <>
              <div className="crop-container">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={onCropChange}
                  onZoomChange={onZoomChange}
                  onCropComplete={onCropCompleteCallback}
                />
              </div>

              <div className="crop-controls">
                <label className="zoom-label">
                  Zoom
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(parseFloat(e.target.value))}
                    className="zoom-slider"
                  />
                </label>
              </div>
            </>
          )}
        </div>

        {imageSrc && (
          <div className="photo-upload-modal-footer">
            <button className="btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button className="btn-apply" onClick={handleApply}>
              Apply Photo
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoUploadModal;
